import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { ImageIcon } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { ImageUpload } from "../../../components/common/ImageUpload";
import { useAdminT } from "../../../hooks/useAdminT";
import { RemoteImage } from "../../../core/components/RemoteImage";

// Helper for translations array
const getTrans = (translations: any[], locale: string, field: string) =>
    translations?.find((t: any) => t.locale === locale)?.[field] || "";

export default function BannersPage() {
    const { t } = useAdminT();
    const queryClient = useQueryClient();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);

    const { register, handleSubmit, reset, control } = useForm();

    // Returns RAW Prisma: { id, image, link, active, order, translations: [{locale, title, description, ctaText}] }
    const { data: banners, isLoading } = useQuery({
        queryKey: ["banners"],
        queryFn: () => api.get("/api/admin/cms/banners").then(res => res.data.data)
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/cms/banners", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["banners"] });
            handleClose();
        }
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/cms/banners/${editingId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["banners"] });
            handleClose();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/cms/banners/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["banners"] });
            setDeleteTarget(null);
        }
    });

    const handleClose = () => {
        setIsDrawOpen(false);
        setEditingId(null);
        reset();
    };

    const handleEdit = useCallback((item: any) => {
        setEditingId(item.id);
        reset({
            active: item.active ?? true,
            order: item.order ?? 0,
            image: item.image || "",
            link: item.link || "",
            titleEn: getTrans(item.translations, "en", "title"),
            titleAr: getTrans(item.translations, "ar", "title"),
            descriptionEn: getTrans(item.translations, "en", "description"),
            descriptionAr: getTrans(item.translations, "ar", "description"),
            ctaTextEn: getTrans(item.translations, "en", "ctaText"),
            ctaTextAr: getTrans(item.translations, "ar", "ctaText"),
        });
        setIsDrawOpen(true);
    }, [reset]);

    const onSubmit = (data: any) => {
        const payload = {
            active: data.active ?? true,
            order: Number(data.order) || 0,
            image: data.image,
            link: data.link || null,
            translations: [
                { locale: "en", title: data.titleEn, description: data.descriptionEn || null, ctaText: data.ctaTextEn || null },
                { locale: "ar", title: data.titleAr, description: data.descriptionAr || null, ctaText: data.ctaTextAr || null },
            ],
        };

        if (editingId) {
            updateMutation.mutate(payload);
        } else {
            createMutation.mutate(payload);
        }
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("banners.title")}
                description={t("banners.subtitle")}
                actions={[
                    {
                        label: t("banners.add"),
                        icon: ImageIcon,
                        onClick: () => setIsDrawOpen(true)
                    }
                ]}
            />

            <AdminTable
                data={banners}
                isLoading={isLoading}
                columns={[
                    {
                        header: t("common.image"), cell: (b) => b.image
                            ? <RemoteImage src={b.image} className="w-24 h-12 object-cover rounded" />
                            : <div className="w-24 h-12 bg-slate-100 rounded" />,
                        enableSorting: false,
                    },
                    { header: t("common.title"), cell: (b) => getTrans(b.translations, "en", "title") || "-" },
                    { header: t("banners.fields.order"), accessorKey: "order" as any },
                    { header: t("common.status"), cell: (b) => <StatusBadge status={b.active} /> },
                ]}
                onEdit={handleEdit}
                onDelete={(b) => setDeleteTarget(b)}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("banners.delete")}
                message={`${t("common.confirmProceed")} ${getTrans(deleteTarget?.translations, "en", "title") || ""}`}
                confirmText="Delete"
                isLoading={deleteMutation.isPending}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingId ? t("banners.edit") : t("banners.add")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border rounded hover:bg-slate-50">{t("common.cancel")}</button>
                        <button onClick={handleSubmit(onSubmit)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">
                            {editingId ? t("common.save") : t("common.create")}
                        </button>
                    </>
                }
            >
                <form className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">{t("banners.fields.order")}</label>
                            <input type="number" {...register("order")} className="mt-1 w-full px-3 py-2 border rounded-md" />
                        </div>
                        <div className="flex items-center pt-6">
                            <input type="checkbox" {...register("active")} id="active" className="h-4 w-4" />
                            <label htmlFor="active" className="ml-2 text-sm font-medium">{t("common.active")}</label>
                        </div>
                    </div>

                    {/* Image Upload (replacing raw URL input) */}
                    <div>
                        <label className="block text-sm font-medium mb-1">{t("banners.fields.image")}</label>
                        <Controller
                            name="image"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <ImageUpload value={field.value} onChange={field.onChange} />
                            )}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium">{t("banners.fields.link")}</label>
                        <input {...register("link")} className="mt-1 w-full px-3 py-2 border rounded-md" />
                    </div>

                    {/* English */}
                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        <h3 className="font-medium text-slate-800 mb-3">English</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">Title</label>
                                <input {...register("titleEn", { required: true })} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">Description</label>
                                <textarea {...register("descriptionEn")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" rows={2} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">CTA Text</label>
                                <input {...register("ctaTextEn")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" placeholder="e.g. Learn More" />
                            </div>
                        </div>
                    </div>

                    {/* Arabic */}
                    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" dir="rtl">
                        <h3 className="font-medium text-slate-800 mb-3">العربية</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">العنوان</label>
                                <input {...register("titleAr", { required: true })} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">الوصف</label>
                                <textarea {...register("descriptionAr")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" rows={2} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium uppercase text-slate-500">نص الزر</label>
                                <input {...register("ctaTextAr")} className="mt-1 w-full px-3 py-2 border rounded-md bg-white" />
                            </div>
                        </div>
                    </div>
                </form>
            </SlideOver>
        </div>
    );
}
